<template>
    <main
        v-if="article"
        class="bg-gray-100 page-product-details"
    >
        <Head>
            <Title>{{ article.name }}</Title>
            <Meta
                name="description"
                :content="article.name + ' ' + article.name2"
            />
        </Head>

        <div class="container mx-auto mt-4">
            <Breadcrumb :title="article.name + ' ' + article.name2"/>
        </div>

        <section
            class="container block mx-auto gap-x-8 gap-y-16 py-4 text-sm md:space-y-4 lg:grid lg:grid-cols-5 lg:py-15 lg:space-y-0"
        >
            <div class="col-span-3">
                <div class="gap-8 select-none md:flex">
                    <div class="thumbnail-wrapper">
                        <div
                            v-if="countImagesPlus3D > maxThumbnailImages"
                            class="thumbnail-arrow-up"
                            @click="scrollUp"
                        >
                            <IconChevronUp
                                :tw-class="reachedThumbnailStart ? 'w-6 h-6 stroke-1 stroke-neutral-400' : 'w-6 h-6 stroke-2 stroke-secondary'"
                            />
                        </div>
                        <div class="thumbnail-track">
                            <div
                                ref="thumbnails"
                                class="thumbnails"
                                :style="{ top: distance+'px' }"
                            >
                                <div
                                    v-for="(image, index) in article.images"
                                    :key="image.id ?? index"
                                    class="thumbnail relative"
                                    @click="selectImage(index)"
                                >
                                    <NuxtPicture
                                        provider="apiImage"
                                        :src="image._id + '/' + encodeURIComponent(image.name)"
                                    />
                                </div>
                                <div
                                    v-if="article.configuratorId || hasCombinationModel.hasCombinationModel"
                                    class="thumbnail bg-primary! flex items-center justify-center"
                                    @click="show3D = true; load3D = true;"
                                >
                                    <Icon3D :tw-class="'fill-white'"/>
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="countImagesPlus3D > maxThumbnailImages"
                            class="thumbnail-arrow-down"
                            @click="scrollDown"
                        >
                            <IconChevronDown
                                :tw-class="reachedThumbnailEnd ? 'w-6 h-6 stroke-1 stroke-neutral-400' : 'w-6 h-6 stroke-2 stroke-secondary'"
                            />
                        </div>
                    </div>

                    <div class="relative md:w-[85%] bg-white shadow-md rounded-lg">
                        <div
                            class="w-full overflow-hidden rounded-lg aspect-4/3 flex items-center justify-center lg:max-h-full"
                        >
                            <Viewer
                                v-if="(article.configuratorId || hasCombinationModel) && (article.images.length===0 || load3D)"
                                :id="article.configuratorId"
                                :has-combination-model="hasCombinationModel"
                                :class="['w-full h-full aspect-square cursor-move', {'hidden': !show3D && article.images.length > 0}]"
                            />
                            <UModal
                                :close="{
                                  color: 'secondary',
                                  variant: 'solid',
                                  class: 'rounded-full'
                                }"
                                :content="{
                                  class: '2xl:!max-w-4xl'
                                }"
                            >
                              <div
                                    v-if="currentImage && !show3D"
                              >
                                <NuxtPicture
                                    provider="apiImage"
                                    :src="`${currentImage._id}/${encodeURIComponent(currentImage.name)}`"
                                    :alt="currentImage.alternativeText ?? ''"
                                    :img-attrs="{ draggable: false, class: 'object-contain aspect-[4/3] justify-self-center cursor-zoom-in' }"
                                />
                                <img
                                    v-if="getAiLabelSrc(currentImage)"
                                    :src="getAiLabelSrc(currentImage)!"
                                    alt="AI Label"
                                    class="absolute bottom-2 right-2 h-9 md:h-10 pointer-events-none select-none"
                                >
                              </div>
                                <template #body>
                                  <div
                                        v-if="currentImage && !show3D"
                                  >
                                    <NuxtPicture
                                        provider="apiImage"
                                        :src="`${currentImage._id}/${encodeURIComponent(currentImage.name)}`"
                                        :alt="currentImage.alternativeText ?? ''"
                                        :img-attrs="{ draggable: false, class: 'object-contain aspect-[4/3] justify-self-center cursor-zoom-in' }"
                                    />
                                    <img
                                        v-if="getAiLabelSrc(currentImage)"
                                        :src="getAiLabelSrc(currentImage)!"
                                        alt="AI Label"
                                        class="absolute bottom-2 right-2 h-9 md:h-10 pointer-events-none select-none"
                                    >
                                  </div>
                                </template>
                            </UModal>
                            <UModal
                                :close="{
                                  color: 'secondary',
                                  variant: 'solid',
                                  class: 'rounded-full'
                                }"
                                title="Jetzt scannen"
                            >
                                <UButton
                                    class="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2 whitespace-nowrap"
                                    color="secondary"
                                    variant="outline"
                                >
                                    <template #leading>
                                        <IconQRCode tw-class="w-6 h-6 fill-secondary"/>
                                    </template>
                                    Mobile AR-Ansicht
                                </UButton>
                                <template #body>
                                    <ARModal :article="article"/>
                                </template>
                            </UModal>
                        </div>
                    </div>
                </div>
                <div class="flex gap-12 items-center justify-center py-8">
                    <NuxtPicture src="icons/icon-made-in-germany.png"/>
                    <NuxtPicture src="icons/icon-avpq.png"/>
                    <NuxtPicture
                        src="Sauerland-Spielgeraete_Siegel-2023_210923.png"
                        :img-attrs="{style: 'height: 90px'}"
                    />
                    <NuxtPicture
                        src="Sauerland-Spielgeraete_Siegel-2023_7.png"
                        :img-attrs="{style: 'height: 90px'}"
                    />
                </div>
            </div>

            <div class="col-span-2 space-y-8">
              <h1 class="h3 text-primary! font-bold!">
                    {{ article.name }} - {{ article.name2 }}
                </h1>
                <div class="grid grid-cols-5 text-base">
                    <span class="col-span-2">Art.Nr. {{ article.articleNumber }}</span>
                    <template v-if="article.price >= 25000">
                        <span class="font-bold text-right text-secondary col-span-3">Preis auf Anfrage</span>
                    </template>
                    <div
                        v-else-if="article.totalPrice != article.price"
                        class="grid grid-cols-2 col-span-3"
                    >
                        <template v-if="StringTools.showPrice(article)">
                            <span class="text-sm text-gray-400">Grundpreis</span>
                            <span class="text-sm text-gray-400 text-right">{{
                                    StringTools.currencyString(article.price)
                                }}{{ article.unit ? ' / ' + article.unit : '' }}</span>
                            <span class="text-sm text-gray-400">zzgl. Pfostenschuhe</span>
                            <span class="text-sm text-gray-400 text-right">{{ StringTools.currencyString(article.totalPrice - article.price) }}</span>
                            <div class="text-secondary col-span-2 flex flex-row">
                                <div class="basis-1/3">
                                    <span class="font-bold">Gesamtpreis</span>
                                    <div class="text-[10px]/[10px]">
                                        netto ab Werk
                                    </div>
                                </div>
                                <div class="basis-2/3 font-bold text-right text-secondary">
                                    ab {{
                                        StringTools.currencyString(article.totalPrice)
                                    }}{{ article.unit ? ' / ' + article.unit : '' }}
                                </div>
                            </div>
                        </template>
                    </div>
                    <template v-else-if="article.totalPrice > 0">
                        <span
                            v-if="StringTools.showPrice(article)"
                            class="font-bold text-right text-secondary col-span-3"
                        >
                            ab {{
                                StringTools.currencyString(article.totalPrice)
                            }}{{ article.unit ? ' / ' + article.unit : '' }} netto ab Werk
                        </span>
                    </template>
                    <template v-else>
                        <span class="font-bold text-right text-secondary col-span-3">Preis auf Anfrage</span>
                    </template>
                </div>

                <!-- Varianten -->
                <div>
                    <div class="font-bold">
                        Variante:
                    </div>
                    <USelect
                        v-if="variantItems.length > 1"
                        v-model="currentVariantId"
                        :items="variantItems"
                        class="w-full rounded-lg overflow-hidden"
                    />
                    <UInput
                        v-else
                        :model-value="selectedVariantLabel"
                        variant="none"
                        readonly
                        class="w-full shadow-lg rounded bg-white rounded-lg overflow-hidden"
                    />
                </div>

                <!-- Buttons -->
                <div class="grid gap-4 items-center sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2 2xl:h-9">
                    <div class="h-full">
                        <UButton
                            class="w-full h-9 justify-center"
                            color="secondary"
                            variant="outline"
                            leading-icon="i-lucide:send"
                            @click="openModalContact = true"
                        >
                            Kontakt aufnehmen
                        </UButton>
                        <UModal
                            v-model:open="openModalContact"
                            :content="{
                              class: '2xl:!max-w-4xl'
                            }"
                        >
                            <template #body>
                                <ModalContact
                                    :article="article"
                                    @sent="messageSent"
                                />
                            </template>
                        </UModal>
                    </div>
                    <div class="h-full">
                        <UTooltip :text="onWishlist ? 'Vom Merkzettel entfernen' : 'Auf den Merkzettel'">
                            <UButton
                                class="w-full h-9 justify-center"
                                color="secondary"
                                variant="outline"
                                @click="toggleWishlist()"
                            >
                                <template #leading>
                                    <IconStar
                                        :tw-class="onWishlist
                                                ? 'w-6 h-6 stroke-none fill-secondary'
                                                : 'w-6 h-6 stroke-secondary stroke-2 fill-none'"
                                    />
                                </template>
                                Auf den Merkzettel
                            </UButton>
                        </UTooltip>
                    </div>
                </div>

                <!-- Spezifikationen (Auszug) -->
                <div class="specifications divide-y divide-neutral-200">
                    <div
                        v-for="(attribute, index) in attributes.slice(0, 4)"
                        :key="index"
                        class="pb-4"
                    >
                        <span><NuxtPicture
                            :src="'/images/attributes/' + attribute.image"
                            :alt="attribute.title"
                        />{{ attribute.title }}</span>
                        <span>{{ attribute.value }}</span>
                    </div>
                </div>
                <div
                    class="flex justify-center gap-4 underline text-secondary cursor-pointer"
                    @click="scrollTo(descriptionRefEl)"
                >
                    <span>Weitere Details</span>
                    <div
                        class="animate-bounce bg-white p-2 w-10 h-10 shadow-lg rounded-full flex items-center justify-center"
                    >
                        <IconArrowDown/>
                    </div>
                </div>
            </div>
        </section>

        <!-- Beschreibung / Downloads -->
        <section class="bg-white">
            <div
                ref="descriptionRefEl"
                class="container mx-auto space-y-16 py-4 lg:py-16"
            >
                <div
                    id="description"
                    class="block lg:grid lg:grid-cols-5 gap-x-8 gap-y-16 text-sm"
                >
                    <div class="col-span-3 space-y-4 py-4 lg:py-0">
                        <h2>Beschreibung</h2>
                        <div
                            class="space-y-4"
                            v-html="article.description"
                        />
                    </div>
                    <div id="downloads">
                        <h2>Downloads</h2>
                        <div v-if="hasSpecSheet">
                            <a
                                class="cursor-pointer text-neutral-950!"
                                @click.prevent="downloadSpecSheet(article)"
                            >
                                <NuxtPicture src="/images/icons/icon-file-pdf.svg"/>
                                <span>Produktdatenblatt</span>
                            </a>
                        </div>
                        <div>
                            <a
                                class="cursor-pointer text-neutral-950!"
                                @click="showTenderInformation"
                            >
                                <NuxtPicture src="/images/icons/icon-file-pdf.svg"/>
                                <span>Ausschreibungsinformationen</span>
                            </a>
                        </div>
                        <div>
                            <a
                                class="cursor-pointer text-neutral-950!"
                                @click="openModalRequest = true"
                            >
                                <NuxtPicture
                                    class="w-11"
                                    src="/images/icons/dxf-file-icon.svg"
                                />
                                <span>Gerne senden wir Ihnen <span class="text-secondary cursor-pointer underline">auf Anfrage</span> kurzfristig tagesaktuelle DWG- oder DXF-Dateien für Ihre Planung zu!</span>
                            </a>
                            <UModal
                                v-model:open="openModalRequest"
                                :close="{
                                  color: 'secondary',
                                  variant: 'solid',
                                  class: 'rounded-full'
                                }"
                                :content="{
                                  class: '2xl:!max-w-4xl'
                                }"
                                title="Anfrage"
                            >
                                <template #body>
                                    <ModalRequest
                                        :article="article"
                                        @sent="messageSent"
                                    />
                                </template>
                            </UModal>
                        </div>
                        <div
                            v-for="(drawing, index) in article.drawings"
                            :key="index"
                            class="space-y-4 divide-y divide-neutral-200"
                        >
                            <NuxtLink
                                :href="pub.apiUrl + drawing.id + '/' + drawing.name"
                                class="text-secondary pb-4"
                            >
                                <NuxtPicture src="/images/icons/icon-file-pdf.svg"/>
                                <span>{{ drawing.name }}</span>
                            </NuxtLink>
                        </div>
                    </div>
                </div>

                <!-- Technische Details -->
                <template v-if="attributes.length">
                    <hr class="hidden lg:block">
                    <div
                        id="technical-details"
                        ref="technicalDetails"
                        class="block lg:grid lg:grid-cols-5 gap-x-8 space-y-4 text-sm"
                    >
                        <div class="col-span-5">
                            <h2>Technische Details</h2>
                        </div>
                        <div class="specifications divide-y divide-neutral-200 xl:columns-2">
                            <div
                                v-for="(attribute, index) in attributes"
                                :key="index"
                                class="pb-4"
                            >
                                <span>
                                    <NuxtPicture
                                    :src="'/images/attributes/' + attribute.image"
                                    :alt="attribute.title"
                                    />
                                    {{ attribute.title }}
                                </span>
                                <span>{{ attribute.value }}</span>
                            </div>
                        </div>
                        <div class="hidden lg:block">
                            <NuxtPicture src="/images/icons/stick-figure-screw.svg"/>
                        </div>
                    </div>
                </template>

                <!-- Bodenbeschaffenheit -->
                <template v-if="article.freeFallHeight">
                    <hr class="hidden lg:block">
                    <div
                        id="surface-condition"
                        class="space-y-4 text-sm"
                    >
                        <h2>Bodenbeschaffenheit</h2>
                        <div class="grid grid-cols-2 space-y-4 gap-8 items-center justify-stretch md:space-y-0 md:flex">
                            <template
                                v-for="(surface, index) in groundConditions"
                            >
                                <div
                                    v-if="article.freeFallHeight <= surface.maxFreeFallHeight"
                                    :key="index"
                                    class="flex gap-2 items-center"
                                >
                                    <span><NuxtPicture :src="'/images/attributes/' + surface.image"/></span>
                                    <span>{{ surface.title }}</span>
                                </div>
                            </template>
                        </div>
                    </div>
                </template>

                <!-- Inklusion -->
                <template v-if="inclusionGroups.length">
                    <hr class="hidden lg:block">
                    <div
                        id="surface-condition"
                        class="space-y-4 text-sm"
                    >
                        <h2>Inklusion</h2>
                        <div class="flex flex-col sm:flex-row gap-8 items-start justify-between">
                            <div
                                v-for="(group, index) in inclusionGroups"
                                :key="index"
                            >
                                <h3 class="font-bold">
                                    {{ group.name }}
                                </h3>
                                <div
                                    v-for="(icon, iconIndex) in group.icons"
                                    :key="iconIndex"
                                    class="flex gap-1 my-1 items-center"
                                >
                                    <NuxtPicture
                                        :src="`/images/icons/inclusion/${icon.filename}`"
                                        :alt="icon.name"
                                        class="w-10 h-10"
                                    />
                                    <span class="ml-2">{{ icon.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <p class="text-sm">
                    Irrtümer und technische Änderungen vorbehalten. // Kein Verkauf an Privatpersonen. Fragen Sie uns
                    an, gerne teilen wir Ihnen einen Händler in Ihrer Umgebung mit.
                </p>
            </div>
        </section>
    </main>
</template>

<script setup lang="ts">
import {StringTools} from '~~/utils/StringTools';
import IconPaperPlane from '~/components/Icons/IconPaperPlane.vue';
import IconStar from '~/components/Icons/IconStar.vue';
import IconArrowDown from '~/components/Icons/IconArrowDown.vue';
import Icon3D from '~/components/Icons/Icon3D.vue';
import {useSpecSheetStore} from '~~/stores/specsheet';
import {useWishlistStore} from '~~/stores/wishlist';
import ARModal from '~/components/Modal/ARModal.vue';
import IconQRCode from '~/components/Icons/IconQRCode.vue';
// import {createWebHistory} from '#vue-router';
import {useCombinationModelStore} from '~~/stores/combinationmodel';
import FilterOptions from '~/components/FilterOptions.vue';
import IconChevronUp from '~/components/Icons/IconChevronUp.vue';
import IconChevronDown from '~/components/Icons/IconChevronDown.vue';
import {getAiLabelSrc} from '~~/utils/ai-image-label';

const route = useRoute();
const router = useRouter();
const {public: pub} = useRuntimeConfig();
const distance = ref(0);
const load3D = ref(false);
const openModalContact = ref(false);
const openModalRequest = ref(false);
const hasCombinationModel = ref(false);
const combinationModelStore = useCombinationModelStore();
const countImagesPlus3D = ref(0);
const maxThumbnailImages = ref(5);
const reachedThumbnailStart = ref(true);
const reachedThumbnailEnd = ref(false);

/**
 * Artikel
 */
const id = computed(() => String(route.params.id));
const apiId = computed(() => `/api/articles/${id.value}`);
const article = await useArticle(apiId.value);

/**
 * Thumbnails
 */
const thumbnails = ref<InstanceType<typeof HTMLElement> | null>(null);
countImagesPlus3D.value = (article.value.configuratorId) ? article.value.images.length + 1 : article.value.images.length;

function scrollUp() {
    if (distance.value < 0) {
        distance.value += (thumbnails.value?.offsetHeight / countImagesPlus3D.value);
    }

    reachedThumbnailStart.value = !(distance.value < 0);
    reachedThumbnailEnd.value = (distance.value < 0);
}

function scrollDown() {
    const diff = thumbnails.value?.parentElement ? thumbnails.value?.parentElement.offsetHeight - thumbnails.value?.offsetHeight : 0;
    if (distance.value > diff) {
        distance.value -= (thumbnails.value?.offsetHeight / countImagesPlus3D.value);
    }

    reachedThumbnailStart.value = !(diff < 0);
    reachedThumbnailEnd.value = (diff < 0);
}

/**
 * Varianten + URL Sync
 */
const currentVariantId = ref('');
watch(() => route.params.id, (v) => {
    currentVariantId.value = String(v ?? '');
}, {immediate: true});

const {
    variantItems,
    findVariantById,
} = useVariants(article);

watch(currentVariantId, (newId) => {
    if (!newId) return;
    const v = findVariantById(newId);
    if (!v) return;
    const slug = StringTools.simplify([v.name, v.name2].filter(Boolean)
        .join(' '));
    // get category slug from article.categories[0].name
    const categorySlug = StringTools.simplify(article.value.categories[0].name);
    router.replace(`/${categorySlug}/${slug}/${newId}`); // replace: Back-Button bleibt clean
});

const selectedVariantLabel = computed(() => {
    const v = findVariantById(currentVariantId.value) ?? findVariantById(String(route.params.id)); // fallback
    return v ? [v.name, v.name2].filter(Boolean)
        .join(' ') : '';
});

/**
 * Technische Details
 */
const {attributes} = useAttributes(article);

/**
 * Bodenbeschaffenheit
 */
const {groundConditions} = useGroundConditions();

/**
 * Inklusion
 */
const {inclusionGroups} = useInclusionIcons(article);

/**
 * PDF-Datenblatt vorhanden?
 */
const {hasSpecSheet} = useSpecSheet(article);
const specSheetStore = useSpecSheetStore();

async function downloadSpecSheet(product: any) {
    if (!product) return;
    await specSheetStore.download(product);
}

/**
 * ScrollTo
 */
const descriptionRefEl = useTemplateRef('descriptionRefEl');

function scrollTo(view: Ref<HTMLElement | null>) {
    view.scrollIntoView({behavior: 'smooth'});
}

const toast = useToast();

function showToast() {
    toast.add({
        title: 'Artikel zum Merkzettel hinzugefügt!',
        color: 'success',
    });
}

/**
 * Product gallery
 */
const images = computed(() => article.value?.images ?? []);
const {
    selectedIndex,
    show3D,
    currentImage,
    selectImage,
    open3D,
    open2D,
} = useProductGallery(images);

/**
 * Sent request message
 */
function messageSent(e: any) {
    toast.add({
        title: e.success ? 'Erfolgreich versendet' : 'Ein Fehler ist aufgetreten',
        description: e.success ? 'Ihre Nachricht wurde erfolgreich versendet. Wir melden uns in Kürze bei Ihnen.' : 'Beim Versenden der Nachricht ist ein Fehler aufgetreten. Versuchen Sie es später noch einmal.',
        color: e.success ? 'success' : 'error',
    });

    if (e.success) {
        openModalContact.value = false;
        openModalRequest.value = false;
    }
}

/**
 * Merkzettel
 */
const wishlistStore = useWishlistStore();
const onWishlist = ref(false);

async function checkHasCombinationModel() {
    hasCombinationModel.value = await combinationModelStore.check(article.value);
}

onMounted(async () => {
    await wishlistStore.init();
    onWishlist.value = wishlistStore.has(article.value);
    if (!article.value.configuratorId) {
        await checkHasCombinationModel();
    }
});

async function toggleWishlist() {
    if (onWishlist.value) {
        await wishlistStore.remove(article.value);
        onWishlist.value = false;
    } else {
        await wishlistStore.add(article.value);
        onWishlist.value = true;
    }
}

/**
 * Ausschreibungsinformationen
 */
function getFilenameFromContentDisposition(cd: string): string | null {
    // RFC 5987: filename*=UTF-8''...
    const mStar = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (mStar?.[1]) return decodeURIComponent(mStar[1].replace(/(^"|"$)/g, ''));

    // fallback: filename="..."
    const m = cd.match(/filename\s*=\s*"?([^"]+)"?/i);
    if (m?.[1]) return m[1];

    return null;
}

async function showTenderInformation(e) {
    e.preventDefault();

    const url = `${pub.apiUrl}${article.value.id}/export_tender_information`;
    const data: any = {};
    const response = await $fetch.raw<ArrayBuffer>(url, {
        method: 'POST',
        body: data,
        responseType: 'arrayBuffer',
    });

    const cd = response.headers.get('content-disposition') || '';
    const filename = getFilenameFromContentDisposition(cd) || 'wishlist.pdf';

    // PDF Blob + Download
    const blob = new Blob([response._data], {type: 'application/pdf'});
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(objectUrl);
}
</script>
